import React from 'react';
import { Link } from 'react-router-dom';

const PatientPortalFeatures = () => {
  const features = [
    {
      title: "Secure Access",
      description: "HIPAA-compliant secure access to your dental records and information",
      icon: "🔒",
      benefits: [
        "End-to-end encryption",
        "Two-factor authentication",
        "Secure messaging",
        "Privacy controls"
      ]
    },
    {
      title: "Appointment Management",
      description: "Easy scheduling and management of your dental appointments",
      icon: "📅",
      benefits: [
        "Online scheduling",
        "Appointment reminders",
        "Waitlist management",
        "Rescheduling options"
      ]
    },
    {
      title: "Treatment Plans",
      description: "Comprehensive view of your treatment plans and progress",
      icon: "📋",
      benefits: [
        "Treatment timeline",
        "Cost estimates",
        "Progress tracking",
        "Document access"
      ]
    },
    {
      title: "Communication Hub",
      description: "Direct communication with your dental care team",
      icon: "💬",
      benefits: [
        "Secure messaging",
        "File sharing",
        "Prescription requests",
        "Emergency contact"
      ]
    }
  ];

  const testimonials = [
    {
      quote: "The patient portal has made it so much easier to manage my dental care. I can schedule appointments, view my treatment plan, and communicate with my dentist all in one place.",
      author: "Sarah M.",
      patient: "Regular Patient"
    },
    {
      quote: "I love being able to access my dental records and communicate with my dental team anytime. It's made my dental care experience much more convenient.",
      author: "John D.",
      patient: "Family Patient"
    }
  ];

  return (
    <div className="pt-24 bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E5F0F3] to-white">
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#1E2A4A] mb-6">
              Patient Portal
            </h1>
            <p className="text-xl text-gray-600">
              Your secure, user-friendly gateway to comprehensive dental care management
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-shadow"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-2xl">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1E2A4A] mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <svg className="w-5 h-5 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-2xl p-8 shadow-xl mb-16">
          <h2 className="text-3xl font-bold text-[#1E2A4A] mb-8 text-center">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: 1,
                title: "Create Account",
                description: "Quick and easy registration process with secure verification"
              },
              {
                step: 2,
                title: "Access Features",
                description: "Immediate access to all portal features and services"
              },
              {
                step: 3,
                title: "Stay Connected",
                description: "Regular updates and notifications about your dental care"
              }
            ].map((step) => (
              <div 
                key={step.step}
                className="text-center p-6 bg-gray-50 rounded-xl"
              >
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-[#1E2A4A] mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              <div className="text-2xl text-orange-500 mb-4">"</div>
              <p className="text-gray-600 mb-6">
                {testimonial.quote}
              </p>
              <div>
                <div className="font-bold text-[#1E2A4A]">
                  {testimonial.author}
                </div>
                <div className="text-gray-600">
                  {testimonial.patient}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="bg-orange-500 rounded-2xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8">
            Access your dental care information anytime, anywhere
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/patient-login"
              className="bg-white text-orange-500 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
            >
              Login to Portal
            </Link>
            <Link 
              to="/contact"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-orange-500 transition-colors"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientPortalFeatures; 